import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Select, MenuItem, FormControl, InputLabel, Chip, IconButton,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, Typography, Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useSelector, useDispatch } from 'react-redux';
import { login } from '../store/authSlice';
import userService from '../services/userService';

const PAGE_SIZE = 20;

const ManageOnboarding = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [total, setTotal] = useState(0);

  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [filterBy, setFilterBy] = useState('name');
  const [filterStatusBy, setFilterStatusBy] = useState('all');
  const [page, setPage] = useState(1);

  const [editingFollowersId, setEditingFollowersId] = useState(null);
  const [newFollowersValue, setNewFollowersValue] = useState('');

  const [showMediaModal, setShowMediaModal] = useState(false);
  const [modalMediaType, setModalMediaType] = useState(null);
  const [modalMediaUrl, setModalMediaUrl] = useState('');

  const fetchAthletes = useCallback(async (pageNum = 1, filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const {
        keyword = appliedQuery,
        filterField = filterBy,
      } = filters;

      // Onboarding focuses on accepted trainers; workout status comes from /trainers.
      const { items, total: totalCount } = await userService.getTrainers({
        skip: (pageNum - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        keyword,
        filterBy: filterField,
        status: 'accepted',
      });

      setAthletes(items);
      setTotal(totalCount);
      setPage(pageNum);
    } catch (err) {
      setError('Failed to fetch athletes: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [appliedQuery, filterBy]);

  useEffect(() => {
    dispatch(login(user));
    fetchAthletes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getOnboardingStatus = (u) => {
    const profileDone = !!u?.profileCompletedAt;
    const videoUploaded = u?.workout && String(u.workout).length > 0;
    const videoApproved = u?.status === 1;

    if (videoUploaded && videoApproved) return { text: 'Onboarding completed', color: '#81c784', bg: 'rgba(76,175,80,0.15)' };
    if (!profileDone) {
      if (u?.profileReminderSecondSentAt) return { text: '2nd profile reminder', color: '#ffb74d', bg: 'rgba(255,152,0,0.15)' };
      if (u?.profileReminderSentAt) return { text: '1st profile reminder', color: '#ffb74d', bg: 'rgba(255,152,0,0.15)' };
      return { text: 'Profile pending', color: '#8b949e', bg: 'rgba(139,148,158,0.15)' };
    }
    if (videoUploaded && !videoApproved) return { text: 'Waiting video approval', color: '#64b5f6', bg: 'rgba(100,181,246,0.15)' };
    if (u?.firstWorkoutReminderSecondSentAt) return { text: '2nd workout reminder', color: '#ffb74d', bg: 'rgba(255,152,0,0.15)' };
    if (u?.firstWorkoutReminderSentAt) return { text: '1st workout reminder', color: '#ffb74d', bg: 'rgba(255,152,0,0.15)' };
    return { text: 'Video pending', color: '#8b949e', bg: 'rgba(139,148,158,0.15)' };
  };

  const performSearch = () => {
    const trimmed = (query || '').trim();
    setAppliedQuery(trimmed);
    fetchAthletes(1, { keyword: trimmed, filterField: filterBy });
  };

  const handleRemoveFilter = () => {
    setQuery('');
    setAppliedQuery('');
    setFilterStatusBy('all');
    fetchAthletes(1, { keyword: '', filterField: filterBy });
  };

  const isFilteredActive = () => (appliedQuery || '').trim() || filterStatusBy !== 'all';
  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const handlePageChange = (_, v) => {
    fetchAthletes(v);
  };

  // Client-side filter for workout-derived status chips on the current page only.
  // Search/name filters are server-side; this keeps the legacy status dropdown usable.
  const visibleAthletes = athletes.filter((it) => {
    if (filterStatusBy === 'onboarding') return it.status === -1;
    if (filterStatusBy === 'pending') return it.status === 0;
    if (filterStatusBy === 'accepted') return it.status === 1;
    if (filterStatusBy === 'rejected') return it.status === 2;
    return true;
  });

  const exportExcel = async () => {
    try {
      const { items } = await userService.getTrainers({
        skip: 0,
        limit: Math.min(Math.max(total, PAGE_SIZE), 5000),
        keyword: appliedQuery,
        filterBy,
        status: 'accepted',
      });
      if (!items || items.length === 0) { alert('No data to export'); return; }
      const headers = ['No', 'Name', 'Email', 'Phone', 'Instagram', 'Followers', 'Category', 'Profile', 'Workout', 'Status'];
      const rows = items.map((u, index) => [
        index + 1, u?.name || '', u?.email || '', u?.phone || '', u?.instagramUsername || '',
        u?.instagramFollowers || '', u?.categories?.join(', ') || '',
        u?.categories?.length > 0 ? 'Updated' : 'Pending', u?.workout?.length > 0 ? u?.workout : 'Pending',
        u?.status === 1 ? 'Accepted' : u?.status === 2 ? 'Rejected' : u?.status === 0 ? 'Pending' : 'Onboarding',
      ]);
      const csvContent = [headers, ...rows].map(e => e.map(v => `"${v}"`).join(",")).join("\n");
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'athletes.csv');
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (err) {
      alert('Failed to export: ' + (err?.message || 'Unknown error'));
    }
  };

  const openMedia = (url, type) => { if (!url) return; setModalMediaUrl(url); setModalMediaType(type); setShowMediaModal(true); };
  const closeModal = () => { setShowMediaModal(false); setModalMediaUrl(''); setModalMediaType(null); };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  if (loading && athletes.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}><CircularProgress sx={{ color: '#2bb6a9' }} /></Box>;
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Field</InputLabel>
          <Select value={filterBy} label="Field" onChange={(e) => setFilterBy(e.target.value)}>
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="instagram">IG</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatusBy} label="Status" onChange={(e) => setFilterStatusBy(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="onboarding">Onboarding</MenuItem>
            <MenuItem value="accepted">Accepted</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && performSearch()} sx={{ minWidth: 180 }} />
        <Button size="small" variant="contained" startIcon={<SearchIcon />} onClick={performSearch}>Search</Button>
        <Button size="small" variant="outlined" color="error" startIcon={<ClearIcon />} onClick={handleRemoveFilter} disabled={!isFilteredActive()}>Clear</Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportExcel}>Export</Button>
        <IconButton size="small" onClick={() => fetchAthletes(page)} sx={{ color: '#8b949e' }}><RefreshIcon /></IconButton>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.25)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={28} sx={{ color: '#2bb6a9' }} />
          </Box>
        )}
        <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>IG</TableCell>
                <TableCell>Followers</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Profile</TableCell>
                <TableCell>Workout</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleAthletes.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: '#8b949e' }}>No athletes found.</TableCell></TableRow>
              ) : visibleAthletes.map((u, index) => {
                const name = u?.name || u?.fullName || '—';
                const email = u?.email || '—';
                const phone = u?.phone || u?.phoneNumber || '—';
                const ig = (u?.instagramUsername || '').replace(/^@/, '');
                const igUrl = ig ? `https://instagram.com/${ig}` : '';
                const workoutUrl = u?.workout || '';
                const status = getOnboardingStatus(u);

                return (
                  <TableRow key={u._id}>
                    <TableCell>{startIndex + index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email}</Typography>
                        {email !== '—' && <IconButton size="small" onClick={() => copyToClipboard(email)} sx={{ color: '#8b949e', p: 0.3 }}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {phone !== '—' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{phone.length > 8 ? phone.slice(0, 8) + '…' : phone}</Typography>
                          <IconButton size="small" onClick={() => copyToClipboard(phone)} sx={{ color: '#8b949e', p: 0.3 }}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton>
                        </Box>
                      ) : '—'}
                    </TableCell>
                    <TableCell>{ig ? <a href={igUrl} target="_blank" rel="noreferrer" style={{ color: '#2bb6a9', fontSize: '0.8rem', textDecoration: 'none' }}>@{ig}</a> : '—'}</TableCell>
                    <TableCell>
                      {editingFollowersId === u._id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField size="small" type="number" value={newFollowersValue} onChange={(e) => setNewFollowersValue(e.target.value)} sx={{ width: 80 }} inputProps={{ min: 0 }} />
                          <IconButton size="small" color="success" onClick={async () => {
                            try {
                              await userService.updateInstagramFollowers(u._id, newFollowersValue);
                              setAthletes((prev) => prev.map((a) => a._id === u._id ? { ...a, instagramFollowers: newFollowersValue } : a));
                              setEditingFollowersId(null);
                            } catch (err) { alert('Failed: ' + (err?.message || 'Unknown error')); }
                          }}><CheckIcon sx={{ fontSize: 16 }} /></IconButton>
                          <IconButton size="small" onClick={() => setEditingFollowersId(null)} sx={{ color: '#8b949e' }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{u?.instagramFollowers ? Number(u.instagramFollowers).toLocaleString() : '—'}</span>
                          <IconButton size="small" onClick={() => { setEditingFollowersId(u._id); setNewFollowersValue(u?.instagramFollowers || ''); }} sx={{ color: '#8b949e', p: 0.3 }}><EditIcon sx={{ fontSize: 14 }} /></IconButton>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{u?.categories?.join(', ') || '—'}</TableCell>
                    <TableCell>
                      {u?.categories?.length > 0
                        ? <Chip label="Updated" size="small" sx={{ bgcolor: 'rgba(76,175,80,0.15)', color: '#81c784', fontWeight: 600 }} />
                        : <Chip label="Pending" size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ffb74d', fontWeight: 600 }} />
                      }
                    </TableCell>
                    <TableCell>
                      {workoutUrl ? (
                        <IconButton size="small" onClick={() => openMedia(workoutUrl, 'video')} sx={{ color: '#ff9800' }}><PlayCircleOutlineIcon fontSize="small" /></IconButton>
                      ) : (
                        <Chip label="Pending" size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ffb74d', fontWeight: 600 }} />
                      )}
                    </TableCell>
                    <TableCell><Chip label={status.text} size="small" sx={{ bgcolor: status.bg, color: status.color, fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {total > PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="caption" sx={{ color: '#8b949e' }}>
            Showing {total === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + PAGE_SIZE, total)} of {total} entries
          </Typography>
          <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} size="small"
            sx={{ '& .MuiPaginationItem-root': { color: '#8b949e' }, '& .Mui-selected': { bgcolor: 'rgba(43,182,169,0.15) !important', color: '#2bb6a9' } }} />
        </Box>
      )}

      <Dialog open={showMediaModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {modalMediaType === 'video' ? 'Video' : 'Image'} Preview
          <IconButton onClick={closeModal} sx={{ color: '#8b949e' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          {modalMediaType === 'video' ? (
            <video controls style={{ width: '100%', maxHeight: '70vh', borderRadius: 8 }}><source src={modalMediaUrl} /></video>
          ) : modalMediaType === 'image' ? (
            <img src={modalMediaUrl} alt="Preview" style={{ width: '100%', objectFit: 'contain', maxHeight: '70vh', borderRadius: 8 }} />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ManageOnboarding;
